import React, { useMemo } from 'react';
import usePageBlocks from '../hooks/usePageBlocks';
import useCurrentPageSlug from '../hooks/useCurrentPageSlug';
import BlockRenderer from './blocks/BlockRenderer';

/**
 * PageBlocksRenderer V7 - Support Blocs Classiques + Globaux + Layout Container
 * 
 * AMÉLIORATION V7 :
 * ✅ Layout container cohérent avec CmsBlocksRenderer
 * ✅ Gap system entre blocs (24px)
 * ✅ Support complet des blocs classiques (text, banner, product_list, etc.)
 * ✅ Support blocs globaux (header, menu, footer)
 * ✅ Filtrage par requireGlobalType pour header/menu/footer (pas d'order)
 * ✅ Zone content : TOUS les blocs sauf global header/menu/footer (pas de filtre d'order)
 * 
 * Props:
 * - pageIdOrSlug: (OPTIONNEL) ID de la page. Si non fourni, détection auto
 * - position: 'header' | 'menu' | 'content' | 'footer' | 'all'
 * - requireGlobalType: Force un type global précis (ex: 'header', 'menu', 'footer')
 * - filterType: Filtrer par type de bloc (legacy)
 * - debug: Logs détaillés
 */

// Configuration du layout - UNIFIED 1200px
const PAGE_BLOCKS_LAYOUT_CONFIG = {
  content: {
    maxWidth: '1200px',    // UNIFIÉ: Container global unique
    paddingX: '16px',
    paddingY: '16px',
    gap: '32px'
  },
  default: {
    maxWidth: '1200px',    // UNIFIÉ: Container global unique
    paddingX: '24px',
    paddingY: '24px',
    gap: '24px'
  }
};

const PageBlocksRenderer = ({ 
  pageIdOrSlug, 
  position = 'all',
  requireGlobalType = null,
  filterType = null,
  debug = false
}) => {
  // Détection automatique du slug
  const autoSlug = useCurrentPageSlug();
  const effectiveSlug = pageIdOrSlug || autoSlug;
  
  const { blocks, loading, error } = usePageBlocks(effectiveSlug);

  // Traitement et séparation des blocs
  const { blocksToRender, stats } = useMemo(() => {
    if (!blocks || blocks.length === 0) {
      if (debug) {
        console.warn(`⚠️  [PageBlocksRenderer] Page ${effectiveSlug}: AUCUN BLOC`);
      }
      return { blocksToRender: [], stats: { total: 0, filtered: 0 } };
    }
    
    // Filtrer par type si nécessaire
    let filteredBlocks = filterType 
      ? blocks.filter(b => b.type === filterType)
      : blocks;
    
    // ========================================
    // LOGIQUE V6 : SUPPORT BLOCS CLASSIQUES + GLOBAUX
    // Fix: Les blocs globaux sont filtrés par TYPE, pas par ORDER
    // ========================================
    
    // Si requireGlobalType est spécifié, filtrer PAR TYPE UNIQUEMENT
    if (requireGlobalType) {
      filteredBlocks = filteredBlocks.filter(b => {
        if (b.type === 'global_block_reference') {
          return b.content?.global_block_type === requireGlobalType;
        }
        return false; // Exclure blocs non-globaux si requireGlobalType
      });
      
      if (debug && filteredBlocks.length > 0) {
        console.log(`🎯 [PageBlocksRenderer V6] Filtre requireGlobalType="${requireGlobalType}": ${filteredBlocks.length} bloc(s)`);
      }
    }
    
    // Pour la zone CONTENT : inclure TOUS les blocs SAUF les global_block_reference header/menu/footer
    else if (position === 'content') {
      filteredBlocks = filteredBlocks.filter(b => {
        // Exclure les blocs globaux header/menu/footer (ils sont gérés ailleurs)
        if (b.type === 'global_block_reference') {
          const globalType = b.content?.global_block_type;
          if (globalType === 'header' || globalType === 'menu' || globalType === 'footer') {
            return false;
          }
        }
        // Inclure tous les autres blocs (classiques + autres globaux)
        return true;
      });
      
      if (debug) {
        console.log(`📦 [PageBlocksRenderer V6] Zone content: ${filteredBlocks.length} bloc(s) (classiques + globaux non-header/menu/footer)`);
      }
    }
    
    // Pour les autres zones (all) : utiliser l'ancien système de filtrage par order
    else if (position === 'all') {
      // Pas de filtre, on garde tout
      if (debug) {
        console.log(`📦 [PageBlocksRenderer V6] Zone all: ${filteredBlocks.length} bloc(s)`);
      }
    }
    
    // Si position est header/menu/footer sans requireGlobalType, on ne devrait pas arriver ici
    else {
      if (debug) {
        console.warn(`⚠️  [PageBlocksRenderer V6] Configuration incorrecte: position="${position}" sans requireGlobalType`);
      }
    }
    
    // Logs détaillés
    if (debug) {
      console.group(`📄 [PageBlocksRenderer V6] Page: ${effectiveSlug}, Position: ${position}`);
      console.log(`   Total blocs: ${blocks.length}`);
      console.log(`   Filtrés: ${filteredBlocks.length}`);
      
      if (requireGlobalType) {
        console.log(`   RequireGlobalType: ${requireGlobalType}`);
      }
      
      // Détail de chaque bloc filtré
      filteredBlocks.forEach((b, i) => {
        const typeLabel = b.type === 'global_block_reference' 
          ? `global → ${b.content?.global_block_type || '?'}`
          : b.type;
        const label = b.content?.title || b.content?.text?.substring(0, 30) || b.label || b.title || 'sans titre';
        console.log(`   [${i}] ${typeLabel} | order: ${b.order || 0} | ${label}`);
      });
      
      console.groupEnd();
    }
    
    // Trier par ordre
    filteredBlocks.sort((a, b) => (a.order || 0) - (b.order || 0));
    
    return {
      blocksToRender: filteredBlocks,
      stats: { total: blocks.length, filtered: filteredBlocks.length }
    };
  }, [blocks, filterType, position, requireGlobalType, effectiveSlug, debug]);

  // Chargement
  if (loading) {
    return null;
  }

  // Erreur
  if (error) {
    console.error(`❌ [PageBlocksRenderer] Erreur page ${effectiveSlug}:`, error);
    return null;
  }

  // Aucun bloc
  if (blocksToRender.length === 0) {
    if (debug && requireGlobalType) {
      console.log(`   → Aucun bloc global type="${requireGlobalType}"`);
    }
    return null;
  }

  // Configuration layout selon la position
  const layoutConfig = position === 'content' 
    ? PAGE_BLOCKS_LAYOUT_CONFIG.content 
    : PAGE_BLOCKS_LAYOUT_CONFIG.default;

  // Pour les blocs globaux (header/menu/footer), pas de container layout
  if (requireGlobalType) {
    return (
      <>
        {blocksToRender.map((block) => (
          <BlockRenderer key={block.id} block={block} />
        ))}
      </>
    );
  }

  // Pour le contenu, appliquer le layout container avec gap
  return (
    <div 
      className="page-blocks-container"
      data-page-blocks-position={position}
      style={{
        maxWidth: layoutConfig.maxWidth,
        marginLeft: 'auto',
        marginRight: 'auto',
        paddingLeft: layoutConfig.paddingX,
        paddingRight: layoutConfig.paddingX,
        paddingTop: layoutConfig.paddingY,
        paddingBottom: layoutConfig.paddingY,
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      <div 
        className="page-blocks-wrapper"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: layoutConfig.gap,
          width: '100%'
        }}
      >
        {blocksToRender.map((block) => (
          <div 
            key={block.id}
            className="page-block-item"
            data-block-id={block.id}
            style={{ width: '100%' }}
          >
            <BlockRenderer block={block} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PageBlocksRenderer;
